import { UserStoreEntry } from './user-store.wire';
import {
    createDefaultUserStorePersistedState,
    UserStoreEntriesState,
    UserStorePersistedState,
} from './user-store.state';

/** True unless `key` is in the exclusion list — shared by both conversions below. */
function isMirroredKey(key: string, unmirroredKeys: readonly string[]): boolean {
    return !unmirroredKeys.includes(key);
}

/**
 * Backend bulk-read response -> the slice's mirrored-entries map. Drops any entry whose key is in
 * `unmirroredKeys` (see {@link USER_STORE_UNMIRRORED_KEYS}): those keys are never written into the
 * slice, so a stale in-memory value from before an app started excluding a key cannot linger.
 */
export function userStoreEntriesToState(
    entries: readonly UserStoreEntry[],
    unmirroredKeys: readonly string[],
): UserStoreEntriesState {
    const state: Record<string, unknown> = {};
    for (const entry of entries) {
        if (isMirroredKey(entry.id, unmirroredKeys)) {
            state[entry.id] = entry.data;
        }
    }
    return state;
}

/**
 * Projects the mirrored entries down to the ones actually allowed to be mirrored, dropping any
 * key in `unmirroredKeys`. Used by `createPersistedUserStoreSelector` as a second line of
 * defence: even if an excluded key somehow ended up in the in-memory slice, it still cannot reach
 * the persisted IndexedDB copy.
 */
export function excludeUnmirroredKeys(
    entries: UserStoreEntriesState,
    unmirroredKeys: readonly string[],
): UserStoreEntriesState {
    if (unmirroredKeys.length === 0) {
        return entries;
    }
    const filtered: Record<string, unknown> = {};
    for (const key of Object.keys(entries)) {
        if (isMirroredKey(key, unmirroredKeys)) {
            filtered[key] = entries[key];
        }
    }
    return filtered;
}

/**
 * Turns whatever the persistence layer hydrated back (`unknown` — see `sliceHydrated`) into a
 * safe {@link UserStorePersistedState}, falling back to the empty default for `undefined` or
 * anything that is not shaped like the expected object. That fallback also covers a persisted
 * payload from before `pendingDeletes` existed — nothing has shipped with that shape, but hydrating
 * defensively here means a future format change degrades to "start fresh" instead of throwing.
 */
export function parseHydratedUserStoreState(data: unknown): UserStorePersistedState {
    if (typeof data !== 'object' || data === null) {
        return createDefaultUserStorePersistedState();
    }
    const candidate = data as Partial<UserStorePersistedState>;
    const entries: UserStoreEntriesState =
        typeof candidate.entries === 'object' && candidate.entries !== null ? candidate.entries : {};
    const pendingDeletes = Array.isArray(candidate.pendingDeletes)
        ? candidate.pendingDeletes.filter((key): key is string => typeof key === 'string')
        : [];
    return { entries, pendingDeletes };
}
